import { useState } from "react";
import { useResourcesContext } from "app/contexts/resources";
import { RoleGuard } from "app/guards";
import { useEmpresaContext } from "../../../empresa-context";
import {
  Button,
  Stack,
  TextField,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  Grid,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Paper,
  Container,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { filterSpaces } from "app/utils";
import { useMaxDate } from "app/hooks";
import { rtnRegex } from "app/utils/regexps";
import {
  DenominacionSocialSelect,
  MunicipioAutocomplete,
  RegistroMercantilInscripcionTextField,
  RegistroMercantilMatriculaTextField,
  RegistroMercantilTomoTextField,
  RepresentacionGeograficaSelect,
} from "app/components";

export function InformacionGeneralForm() {
  const {
    decretosLegislativos,
    formasDePago,
    frecuenciasDePago,
    camarasDeComercio,
  } = useResourcesContext();

  const { data, updateEmpresa, isUpdating } = useEmpresaContext();

  const [empresa, setEmpresa] = useState(data);
  const [isEditing, setEditing] = useState(false);
  const { maxDate } = useMaxDate();

  async function handleSubmit(e) {
    e.preventDefault();

    // if (!isEditing) {
    //   setEditing(true);

    //   return window.scrollTo(0, 0);
    // }

    // try {
    //   const confirmed = window.confirm(
    //     "¿Desea actualizar la información general de la empresa?"
    //   );

    //   if (!confirmed) {
    //     return;
    //   }

    //   await updateEmpresa({ empresa });

    //   setEditing(false);

    //   return window.scrollTo(0, 0);
    // } catch (error) {
    //   console.log(error.message);
    // }
  }

  function handleReset(e) {
    e.preventDefault();

    setEmpresa(data);
    setEditing(false);

    return window.scrollTo(0, 0);
  }

  return (
    <RoleGuard roles={["empresa:update-form"]}>
      <Container maxWidth="xl">
        <Paper
          className="animate__animated animate__fadeIn"
          component="form"
          onSubmit={handleSubmit}
          onReset={handleReset}
          sx={{ padding: 2 }}
          variant="outlined"
        >
          <Grid container spacing={1.5}>
            {/* Datos Generales */}
            <Grid item container spacing={1} xs={12} md={12}>
              <Grid item xs={12}>
                <Typography variant="overline">
                  <strong>Datos Generales</strong>
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <DenominacionSocialSelect
                  value={empresa.denominacion_social_id}
                  onChange={(value) =>
                    setEmpresa((empresa) => ({
                      ...empresa,
                      denominacion_social_id: value,
                    }))
                  }
                  disabled={!isEditing}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  type="text"
                  label="Nombre comercial"
                  value={empresa.empresa_nombre_comercial}
                  onChange={(e) =>
                    setEmpresa((empresa) => ({
                      ...empresa,
                      empresa_nombre_comercial: filterSpaces({
                        value: e.target.value,
                        regex: /\s{2,}/,
                      }),
                    }))
                  }
                  size="small"
                  fullWidth
                  required
                  disabled={!isEditing}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  type="text"
                  label="RTN de la empresa, propietario o representante legal"
                  value={empresa.empresa_rtn}
                  onChange={(e) =>
                    setEmpresa((empresa) => ({
                      ...empresa,
                      empresa_rtn: e.target.value.trim(),
                    }))
                  }
                  inputProps={{
                    pattern: rtnRegex.pattern,
                    title: rtnRegex.title,
                  }}
                  size="small"
                  fullWidth
                  required
                  disabled={!isEditing}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <RepresentacionGeograficaSelect
                  value={empresa.representacion_geografica_id || ""}
                  onChange={(value) =>
                    setEmpresa((empresa) => ({
                      ...empresa,
                      representacion_geografica_id: value,
                    }))
                  }
                  disabled={!isEditing}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  type="number"
                  label="Cantidad de empleados"
                  value={empresa.empresa_cantidad_de_empleados || ""}
                  onChange={(e) =>
                    setEmpresa((empresa) => ({
                      ...empresa,
                      empresa_cantidad_de_empleados: e.target.valueAsNumber,
                    }))
                  }
                  inputProps={{ min: 1 }}
                  size="small"
                  fullWidth
                  required
                  disabled={!isEditing}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl size="small" fullWidth required>
                  <InputLabel id="camara-de-comercio-label">
                    ¿La empresa está registrada en otra Cámara de Comercio?
                  </InputLabel>

                  <Select
                    label="¿La empresa está registrada en otra Cámara de Comercio?"
                    labelId="camara-de-comercio-label"
                    value={empresa.camara_de_comercio_id || ""}
                    onChange={(e) =>
                      setEmpresa((empresa) => ({
                        ...empresa,
                        camara_de_comercio_id: e.target.value,
                      }))
                    }
                    disabled={!isEditing}
                    autoFocus
                  >
                    {camarasDeComercio.results.map((camara) => (
                      <MenuItem
                        key={camara.camara_de_comercio_id}
                        value={camara.camara_de_comercio_id}
                      >
                        {camara.camara_de_comercio_nombre}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl size="small" fullWidth required>
                  <InputLabel id="decreto-label">
                    ¿La empresa está adherida a algún Decreto Legislativo?
                  </InputLabel>

                  <Select
                    label="¿La empresa está adherida a algún Decreto Legislativo?"
                    labelId="decreto-label"
                    value={empresa.decreto_legislativo_id || ""}
                    onChange={(e) =>
                      setEmpresa((empresa) => ({
                        ...empresa,
                        decreto_legislativo_id: e.target.value,
                      }))
                    }
                    disabled={!isEditing}
                  >
                    {decretosLegislativos.results.map((decreto) => (
                      <MenuItem
                        key={decreto.decreto_legislativo_id}
                        value={decreto.decreto_legislativo_id}
                      >
                        {`${decreto.decreto_legislativo_numero} ${
                          decreto.decreto_legislativo_numero ? " - " : ""
                        } ${decreto.decreto_legislativo_nombre}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Registro Mercantil */}
            <Grid item container spacing={1} xs={12} md={12}>
              <Grid item xs={12}>
                <Typography variant="overline">
                  <strong>Registro Mercantil</strong>
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <MunicipioAutocomplete
                  value={empresa.registro_mercantil_ciudad_id}
                  onChange={(value) =>
                    setEmpresa((empresa) => ({
                      ...empresa,
                      registro_mercantil_ciudad_id: value,
                    }))
                  }
                  disabled={!isEditing}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  type="date"
                  label="Fecha"
                  value={empresa.registro_mercantil_fecha}
                  onChange={(e) =>
                    setEmpresa((empresa) => ({
                      ...empresa,
                      registro_mercantil_fecha: e.target.value,
                    }))
                  }
                  size="small"
                  inputProps={{ max: maxDate }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  fullWidth
                  required
                  disabled={!isEditing}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <RegistroMercantilMatriculaTextField
                  ciudadId={empresa.registro_mercantil_ciudad_id}
                  value={empresa.registro_mercantil_matricula}
                  onChange={(value) =>
                    setEmpresa((empresa) => ({
                      ...empresa,
                      registro_mercantil_matricula: value,
                    }))
                  }
                  disabled={!isEditing}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <RegistroMercantilInscripcionTextField
                  ciudadId={empresa.registro_mercantil_ciudad_id}
                  value={empresa.registro_mercantil_inscripcion}
                  onChange={(value) =>
                    setEmpresa((empresa) => ({
                      ...empresa,
                      registro_mercantil_inscripcion: value,
                    }))
                  }
                  disabled={!isEditing}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <RegistroMercantilTomoTextField
                  ciudadId={empresa.registro_mercantil_ciudad_id}
                  value={empresa.registro_mercantil_tomo}
                  onChange={(value) =>
                    setEmpresa((empresa) => ({
                      ...empresa,
                      registro_mercantil_tomo: value,
                    }))
                  }
                  disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  type="number"
                  label="Capital autorizado"
                  value={empresa.registro_mercantil_capital_autorizado}
                  onChange={(e) =>
                    setEmpresa((empresa) => ({
                      ...empresa,
                      registro_mercantil_capital_autorizado:
                        e.target.valueAsNumber,
                    }))
                  }
                  size="small"
                  inputProps={{ min: 0 }}
                  fullWidth
                  required
                  disabled={!isEditing}
                />
              </Grid>
            </Grid>

            {/* Registro CCIA */}
            <Grid item container spacing={1} xs={12} md={12}>
              <Grid item xs={12}>
                <Typography variant="overline">
                  <strong>Registro CCIA</strong>
                </Typography>
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  type="text"
                  label="Código"
                  value={empresa.empresa_id}
                  size="small"
                  fullWidth
                  disabled={!isEditing}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  type="text"
                  label="Folio"
                  value={empresa.registro_ccia_folio}
                  size="small"
                  fullWidth
                  disabled={!isEditing}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  type="text"
                  label="Tomo"
                  value={empresa.registro_ccia_tomo}
                  size="small"
                  fullWidth
                  disabled={!isEditing}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  type="date"
                  label="Fecha de inscripción"
                  value={empresa.registro_ccia_fecha}
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  fullWidth
                  disabled={!isEditing}
                />
              </Grid>

              {/* <Grid item xs={12} md={6}>
                <TextField
                  type="text"
                  label="Pago por Inscripción"
                  value={parseFloat(
                    empresa.registro_ccia_pago_por_inscripcion
                  ).toFixed(2)}
                  size="small"
                  fullWidth
                  disabled={!isEditing}
                  
                  
                />
              </Grid> */}

              {/* <Grid item xs={12} md={6}>
                <TextField
                  type="text"
                  label="Multa"
                  value={parseFloat(empresa.registro_ccia_multa).toFixed(2)}
                  size="small"
                  fullWidth
                  disabled={!isEditing}
                  
                  
                />
              </Grid> */}

              {/* <Grid item xs={12}>
                <TextField
                  type="date"
                  label="Fecha de reinscripción"
                  value={empresa.empresa_reinscripcion_fecha}
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  fullWidth
                  disabled={!isEditing}
                  
                  
                />
              </Grid> */}
            </Grid>

            {/* Afiliacion */}
            {/* <Grid item container spacing={1} xs={12} md={12}>
              <Grid item xs={12}>
                <Typography variant="overline">
                  <strong>Afiliación CCIA</strong>
                </Typography>
              </Grid>
        
              <Grid item xs={12}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={empresa.empresa_afiliada}
                        onChange={(e) =>
                          setEmpresa((empresa) => ({
                            ...empresa,
                            empresa_afiliada: e.target.checked,
                          }))
                        }
                        disabled={!isEditing}
                        
                      />
                    }
                    label="¿La empresa está afiliada a la CCIA?"
                  />
                </FormGroup>
              </Grid>
        
              <Grid item xs={12} md={6}>
                <FormControl size="small" fullWidth required>
                  <InputLabel id="forma-de-pago-label">Forma de pago</InputLabel>
                  <Select
                    label="Forma de pago"
                    labelId="forma-de-pago-label"
                    value={empresa.forma_de_pago_id || ""}
                    onChange={(e) =>
                      setEmpresa((empresa) => ({
                        ...empresa,
                        forma_de_pago_id: e.target.value,
                      }))
                    }
                    disabled={!isEditing || !empresa.empresa_afiliada}
                  >
                    {formasDePago.results.map((forma) => (
                      <MenuItem
                        key={forma.forma_de_pago_id}
                        value={forma.forma_de_pago_id}
                      >
                        {forma.forma_de_pago_nombre}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
        
              <Grid item xs={12} md={6}>
                <FormControl size="small" fullWidth required>
                  <InputLabel id="frecuencia-de-pago-label">
                    Frecuencia de pago
                  </InputLabel>
                  <Select
                    label="Frecuencia de pago"
                    labelId="frecuencia-de-pago-label"
                    value={empresa.frecuencia_de_pago_id || ""}
                    onChange={(e) =>
                      setEmpresa((empresa) => ({
                        ...empresa,
                        frecuencia_de_pago_id: e.target.value,
                      }))
                    }
                    disabled={!isEditing || !empresa.empresa_afiliada}
                  >
                    {frecuenciasDePago.results.map((frecuencia) => (
                      <MenuItem
                        key={frecuencia.frecuencia_de_pago_id}
                        value={frecuencia.frecuencia_de_pago_id}
                      >
                        {frecuencia.frecuencia_de_pago_nombre}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid> */}

            {/* Buttons */}
            {/* <Grid item xs={12}>
              <Stack
                direction="row"
                justifyContent="end"
                spacing={1}
                alignItems="center"
              >
                <LoadingButton
                  type="submit"
                  variant="contained"
                  color="warning"
                  size="small"
                  loading={!!isUpdating}
                >
                  {!isEditing ? "Editar" : "Actualizar"}
                </LoadingButton>
        
                <Button
                  type="reset"
                  variant="outlined"
                  color="inherit"
                  size="small"
                  disabled={!isEditing}
                  
                >
                  Cancelar
                </Button>
              </Stack>
            </Grid> */}
          </Grid>
        </Paper>
      </Container>
    </RoleGuard>
  );
}
